import React, { createContext, useContext, useMemo, useState } from "react";
import type { PaletteMode } from "@mui/material";

/**
 * A deliberately MINIMAL stand-in for Mantis Free's own `contexts/ConfigContext` (docs/337
 * §"Vendor only the scaffold — copy by dependency closure"): the real Mantis `ConfigContext`
 * carries theme-mode/font-family/menu-orientation/i18n/rtl toggles + a menu-open-state API backed
 * by SWR — explicitly on the "skip entirely" list (its own demo-menu-state plumbing). This app
 * needs two pieces of that: whether the nav Drawer is expanded (full labels) or collapsed
 * (icon rail), and the active light/dark palette mode.
 */
interface ConfigState {
  drawerOpen: boolean;
  toggleDrawer: () => void;
  mode: PaletteMode;
  toggleMode: () => void;
}

const ConfigContext = createContext<ConfigState | null>(null);

const DRAWER_STORAGE_KEY = "equipped-admin-web-mantis:drawerOpen";
const MODE_STORAGE_KEY = "equipped-admin-web-mantis:mode";

function readInitialDrawerOpen(): boolean {
  try {
    const stored = window.localStorage.getItem(DRAWER_STORAGE_KEY);
    if (stored != null) return stored === "1";
  } catch {
    /* private-browsing / storage-full — fall through to the default */
  }
  return true;
}

function readInitialMode(): PaletteMode {
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(readInitialDrawerOpen);
  const [mode, setMode] = useState<PaletteMode>(readInitialMode);

  const toggleDrawer = () => {
    setDrawerOpen((open) => {
      const next = !open;
      try {
        window.localStorage.setItem(DRAWER_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* non-fatal */
      }
      return next;
    });
  };

  const toggleMode = () => {
    setMode((current) => {
      const next = current === "light" ? "dark" : "light";
      try {
        window.localStorage.setItem(MODE_STORAGE_KEY, next);
      } catch {
        /* non-fatal */
      }
      return next;
    });
  };

  const value = useMemo(() => ({ drawerOpen, toggleDrawer, mode, toggleMode }), [drawerOpen, mode]);
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigState {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig() must be used within <ConfigProvider>");
  return ctx;
}
